import { uploadFile } from './upload-file';
import { appendCacheBuster } from './append-cache-buster';

export interface BuildBrandMomentUploadUrlParams {
  adminUrl: string;
  projectId: string;
  /** The channel's bot id (`customerId.botName`) — the asset owner. */
  entityId: string;
}

/**
 * Build the REST path the backend expects for a Brand Moment asset PUT.
 * Extracted from the old inline `getOnUpload('assetData')` URL so it is
 * unit-testable and reused by the deferred Save-time upload (PSTUDIO-3710).
 */
export const buildBrandMomentUploadUrl = ({
  adminUrl,
  projectId,
  entityId,
}: BuildBrandMomentUploadUrlParams): string =>
  `${adminUrl}/rest/bot-manager/${projectId}/${entityId}/assets`;

/** Context fields the Brand Moment PUT URL needs. */
export type BrandMomentUploadContextField = 'projectId' | 'botId';

/**
 * Validate the context required to build the Brand Moment PUT URL. Returns the
 * first missing field (so the caller can surface a matching message) or `null`
 * when everything is present.
 */
export const validateBrandMomentUploadContext = ({
  projectId,
  botId,
}: {
  projectId?: string;
  botId?: string;
}): BrandMomentUploadContextField | null => {
  if (!projectId) {
    return 'projectId';
  }
  if (!botId) {
    return 'botId';
  }
  return null;
};

/** Result of the deferred Brand Moment PUT: raw bot payload + normalized id. */
export interface BrandMomentUploadResult {
  /** The `data` (updated bot) returned by the assets endpoint; fed to `updateAssets`. */
  data: unknown;
  /** The uploaded file's normalized name — the asset id to match after resolve. */
  pictureId?: string;
}

/**
 * Perform the real Brand Moment asset PUT now (deferred from file-select to
 * Save). Resolves with the raw `data` + normalized `pictureId` so the caller can
 * run the `updateAssets` step to resolve the final URL; rejects with the
 * transport error so the Save handler can surface it and abort navigation.
 */
export const uploadBrandMomentFile = ({
  url,
  file,
}: {
  url: string;
  file: File;
}): Promise<BrandMomentUploadResult> =>
  new Promise((resolve, reject) => {
    uploadFile({
      url,
      file,
      type: 'assetData',
      load: () => undefined,
      onChange: (payload) =>
        resolve({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - `data` is only present for the assetData endpoint
          data: payload?.data,
          pictureId: payload?.files?.[0]?.name,
        }),
      error: (e) => reject(e),
    });
  });

/**
 * Resolve the URL to store in `brandMomentUrl` after `updateAssets` returns the
 * bot's assets. Finds the asset whose id matches the uploaded `pictureId` and
 * cache-busts its url (a fresh `?v=` forces a same-name re-upload to refetch);
 * returns `null` when nothing matched (caller should not touch the field).
 */
export const resolveBrandMomentUrl = (
  assets: { id: string; url: string }[],
  pictureId?: string
): string | null => {
  const asset = assets.find((a) => a.id === pictureId);
  return asset ? appendCacheBuster(asset.url) : null;
};
